<template>
  <section id="adaAuditor" class="container-fluid mt-3">
    <Breadcrumb :back="'/juniper/clients/edit/' + clientId" />

    <div class="row">
      <div class="col-12">
        <h2 class="mb-4">
          <i class="fas fa-universal-access me-2"></i>
          ADA Auditor
        </h2>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="row">
      <div class="col-12 text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3">Loading audit data...</p>
      </div>
    </div>

    <template v-else>
      <!-- Compliance Dashboard -->
      <div class="row mb-4">
        <!-- Compliance Score Card -->
        <div class="col-md-4">
          <div class="card shadow-sm">
            <div class="card-body text-center">
              <h5 class="card-title">Overall Compliance Score</h5>
              <div class="display-1 mb-3" :class="scoreClass">
                {{ latestAudit && latestAudit.compliance_score != null ? latestAudit.compliance_score : '--' }}
              </div>
              <p class="text-muted mb-3">Out of 100</p>

              <!-- Separate Scores -->
              <div v-if="latestAudit" class="row g-2 mb-3">
                <div class="col-6">
                  <div class="p-2 bg-light rounded">
                    <small class="text-muted d-block">
                      <i class="fas fa-robot me-1"></i>
                      IBM Code Score
                    </small>
                    <div class="h4 mb-0" :class="getScoreClass(latestAudit.ibm_code_score)">
                      {{ latestAudit.ibm_code_score != null ? latestAudit.ibm_code_score : '--' }}
                    </div>
                  </div>
                </div>
                <div class="col-6">
                  <div class="p-2 bg-light rounded">
                    <small class="text-muted d-block">
                      <i class="fas fa-wave-square me-1"></i>
                      WAVE Visual Score
                    </small>
                    <div class="h4 mb-0" :class="getScoreClass(latestAudit.wave_visual_score)">
                      {{ latestAudit.wave_visual_score != null ? latestAudit.wave_visual_score : '--' }}
                    </div>
                  </div>
                </div>
              </div>

              <small v-if="latestAudit" class="text-muted d-block mb-2">
                Last scanned: {{ formatDate(latestAudit.created_at) }}
              </small>
              <span v-if="latestAudit" class="badge" :class="auditTypeBadgeClass(latestAudit.audit_type)">
                {{ auditTypeLabel(latestAudit.audit_type) }}
              </span>
              <!-- Remediation progress -->
              <div v-if="remediationProgress && remediationProgress.total > 0" class="mt-2">
                <small class="text-muted">Remediation: {{ remediationProgress.reviewed }}/{{ remediationProgress.total }}</small>
                <div class="progress mt-1" style="height:6px;">
                  <div class="progress-bar bg-success" :style="{ width: remediationProgress.percentage + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Issues Summary -->
        <div class="col-md-4">
          <div class="card shadow-sm">
            <div class="card-body">
              <h5 class="card-title">Issues Found</h5>
              <div class="d-flex justify-content-between mb-2">
                <span><i class="fas fa-times-circle text-danger me-2"></i>Errors</span>
                <strong class="text-danger">{{ latestAudit && latestAudit.total_errors || 0 }}</strong>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span><i class="fas fa-exclamation-triangle text-warning me-2"></i>Warnings</span>
                <strong class="text-warning">{{ latestAudit && latestAudit.total_warnings || 0 }}</strong>
              </div>
              <div class="d-flex justify-content-between">
                <span><i class="fas fa-info-circle text-info me-2"></i>Notices</span>
                <strong class="text-info">{{ latestAudit && latestAudit.total_notices || 0 }}</strong>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="col-md-4">
          <div class="card shadow-sm">
            <div class="card-body">
              <h5 class="card-title">Actions</h5>
              <!-- Audit Type Selection -->
              <div class="mb-3">
                <label class="form-label small"><strong>Audit Type:</strong></label>
                <div class="form-check">
                  <input class="form-check-input" type="radio" name="auditType" id="auditTypeFull" value="full" v-model="auditType">
                  <label class="form-check-label" for="auditTypeFull">
                    Full Website Audit
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="radio" name="auditType" id="auditTypeHomepage" value="homepage" v-model="auditType">
                  <label class="form-check-label" for="auditTypeHomepage">
                    Homepage Only
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="radio" name="auditType" id="auditTypeSpecific" value="specific" v-model="auditType">
                  <label class="form-check-label" for="auditTypeSpecific">
                    Specific Pages
                  </label>
                </div>
              </div>

              <!-- Specific Pages Selection (shown only when audit type is 'specific') -->
              <div v-if="auditType === 'specific'" class="mb-3">
                <label class="form-label small"><strong>Select Pages:</strong></label>
                <select class="form-select form-select-sm" v-model="selectedPages" multiple size="5">
                  <option v-for="page in clientPages" :key="page.url" :value="page.url">
                    {{ page.title || page.url }}
                  </option>
                </select>
                <small class="text-muted">Hold Ctrl/Cmd to select multiple pages</small>
              </div>

              <!-- WAVE Analysis Option -->
              <div class="mb-3 mt-2">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="includeWave" v-model="includeWave">
                  <label class="form-check-label" for="includeWave">
                    <i class="fas fa-wave-square me-1"></i>
                    Include WAVE Analysis <small class="text-muted">(3 credits/page)</small>
                  </label>
                </div>
                <small v-if="waveCreditsRemaining !== null" class="text-muted ms-4">
                  <i class="fas fa-coins me-1"></i>
                  {{ waveCreditsRemaining }} credits remaining
                </small>
              </div>

              <button
                class="btn btn-primary w-100 mb-2"
                @click="startAudit"
                :disabled="isRunning"
              >
                <i class="fas fa-play me-2"></i>
                {{ isRunning ? 'Audit Running...' : 'Run Audit Now' }}
              </button>
              <button
                class="btn btn-outline-secondary w-100 mb-2"
                @click="showScheduleModal = true"
              >
                <i class="fas fa-calendar-alt me-2"></i>
                Schedule Audits
              </button>
              <button
                class="btn btn-outline-info w-100 mb-2"
                @click="exportReport('client')"
                :disabled="!latestAudit"
              >
                <i class="fas fa-file-pdf me-2"></i>
                Export Client Report
              </button>
              <button
                class="btn btn-outline-secondary w-100"
                @click="exportReport('technical')"
                :disabled="!latestAudit"
              >
                <i class="fas fa-file-code me-2"></i>
                Export Technical Report
              </button>

            </div>
          </div>
        </div>
      </div>

      <!-- Progress Bar -->
      <div class="row mb-4" v-if="isRunning && latestAudit">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h5 class="card-title mb-0">
                  <i class="fas fa-spinner fa-spin me-2"></i>
                  Audit in Progress
                </h5>
                <span class="badge bg-info">
                  {{ latestAudit.pages_scanned || 0 }} / {{ latestAudit.total_pages_to_scan || 0 }} pages
                </span>
              </div>

              <div class="progress mb-2" style="height: 30px;">
                <div
                  class="progress-bar progress-bar-striped progress-bar-animated bg-info"
                  role="progressbar"
                  :style="{ width: (latestAudit.progress_percentage || 0) + '%' }"
                  :aria-valuenow="latestAudit.progress_percentage || 0"
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  <strong>{{ latestAudit.progress_percentage || 0 }}%</strong>
                </div>
              </div>

              <small class="text-muted" v-if="latestAudit.current_page_url">
                <i class="fas fa-globe me-1"></i>
                Currently scanning: <code>{{ truncateUrl(latestAudit.current_page_url) }}</code>
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Trend Chart -->
      <div class="row mb-4" v-if="trendData.length > 0">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-body">
              <h5 class="card-title">Compliance Trend</h5>
              <canvas ref="trendChart"></canvas>
            </div>
          </div>
        </div>
      </div>

      <!-- Audit History -->
      <div class="row mb-4" v-if="trendData.length > 0">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-body">
              <h5 class="card-title">Audit History</h5>
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th style="width: 40px;"></th>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Pages</th>
                      <th>Overall Score</th>
                      <th>IBM Code</th>
                      <th>WAVE Visual</th>
                      <th>Errors</th>
                      <th>Warnings</th>
                      <th>Notices</th>
                      <th style="width: 100px;">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="audit in trendData">
                      <!-- Main audit row -->
                      <tr
                        :key="audit.id"
                        @click="toggleAuditExpand(audit.id)"
                        style="cursor: pointer;"
                        :class="{ 'table-active': expandedAuditId === audit.id }"
                      >
                        <td>
                          <i
                            class="fas"
                            :class="expandedAuditId === audit.id ? 'fa-chevron-down' : 'fa-chevron-right'"
                          ></i>
                        </td>
                        <td>{{ formatDate(audit.created_at) }}</td>
                        <td>
                          <span class="badge" :class="auditTypeBadgeClass(audit.audit_type)">
                            {{ auditTypeLabel(audit.audit_type) }}
                          </span>
                        </td>
                        <td>{{ audit.total_pages_scanned }}</td>
                        <td>
                          <strong :class="getScoreClass(audit.compliance_score)">
                            {{ audit.compliance_score }}
                          </strong>
                        </td>
                        <td>
                          <span :class="getScoreClass(audit.ibm_code_score)">
                            {{ audit.ibm_code_score != null ? audit.ibm_code_score : '--' }}
                          </span>
                        </td>
                        <td>
                          <span :class="getScoreClass(audit.wave_visual_score)">
                            {{ audit.wave_visual_score != null ? audit.wave_visual_score : '--' }}
                          </span>
                        </td>
                        <td><span class="badge bg-danger">{{ audit.total_errors }}</span></td>
                        <td><span class="badge bg-warning">{{ audit.total_warnings }}</span></td>
                        <td><span class="badge bg-info">{{ audit.total_notices }}</span></td>
                        <td>
                          <button
                            class="btn btn-sm btn-outline-primary"
                            @click.stop="exportHistoryReport(audit.id)"
                            title="Export PDF Report"
                          >
                            <i class="fas fa-file-pdf"></i>
                          </button>
                        </td>
                      </tr>

                      <!-- Expanded results row -->
                      <tr
                        v-if="expandedAuditId === audit.id"
                        :key="'expanded-' + audit.id"
                        class="expanded-row"
                      >
                        <td colspan="11" class="p-0">
                          <div class="expanded-content">
                            <div v-if="loadingExpandedAudit" class="text-center py-3">
                              <div class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                              </div>
                              <span class="ms-2">Loading audit results...</span>
                            </div>
                            <div v-else-if="expandedAuditResults.length > 0">
                              <h6 class="mb-3">
                                <i class="fas fa-list-alt me-2"></i>
                                Page-by-Page Results
                              </h6>
                              <div class="table-responsive">
                                <table class="table table-sm table-bordered bg-white">
                                  <thead>
                                    <tr>
                                      <th>Page</th>
                                      <th style="width: 100px;">Errors</th>
                                      <th style="width: 100px;">Warnings</th>
                                      <th style="width: 100px;">Notices</th>
                                      <th style="width: 100px;">Details</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="result in expandedAuditResults" :key="result.id">
                                      <td>
                                        <div class="d-flex align-items-center">
                                          <i class="fas fa-file-alt me-2 text-muted"></i>
                                          <div>
                                            <div class="fw-bold">{{ result.page_title }}</div>
                                            <small class="text-muted text-truncate d-block" style="max-width: 400px;">
                                              {{ result.page_url }}
                                            </small>
                                          </div>
                                        </div>
                                      </td>
                                      <td class="text-center">
                                        <span class="badge bg-danger">{{ result.error_count }}</span>
                                      </td>
                                      <td class="text-center">
                                        <span class="badge bg-warning">{{ result.warning_count }}</span>
                                      </td>
                                      <td class="text-center">
                                        <span class="badge bg-info">{{ result.notice_count }}</span>
                                      </td>
                                      <td class="text-center text-nowrap">
                                        <button
                                          class="btn btn-sm btn-outline-primary me-1"
                                          @click.stop="viewDetails(result)"
                                        >
                                          <i class="fas fa-eye me-1"></i>
                                          View
                                        </button>
                                        <button
                                          :class="hasWorkOrder(result) ? 'btn btn-sm btn-success' : 'btn btn-sm btn-outline-secondary'"
                                          @click.stop="generatePageWorkOrder(result)"
                                          :disabled="result._generatingWO"
                                          :title="hasWorkOrder(result) ? 'View AI Work Order' : 'Generate AI Work Order'"
                                        >
                                          <i :class="result._generatingWO ? 'fas fa-spinner fa-spin' : 'fas fa-robot'"></i>
                                        </button>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            <div v-else class="alert alert-info mb-0">
                              <i class="fas fa-info-circle me-2"></i>
                              No results available for this audit.
                            </div>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Results Table -->
      <div class="row" v-if="auditResults.length > 0">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 class="card-title mb-0">Audit Results by Page</h5>
                  <small class="text-muted" v-if="latestAudit">
                    {{ formatDate(latestAudit.created_at) }} -
                    <span class="badge" :class="auditTypeBadgeClass(latestAudit.audit_type)">
                      {{ auditTypeLabel(latestAudit.audit_type) }}
                    </span>
                  </small>
                </div>
                <div>
                  <select class="form-select form-select-sm" v-model="filterType">
                    <option value="all">All Issues</option>
                    <option value="errors">Errors Only</option>
                    <option value="warnings">Warnings Only</option>
                    <option value="notices">Notices Only</option>
                  </select>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th>Page URL</th>
                      <th>Page Title</th>
                      <th class="text-center">Errors</th>
                      <th class="text-center">Warnings</th>
                      <th class="text-center">Notices</th>
                      <th class="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="result in filteredResults" :key="result.id">
                      <td>
                        <a :href="result.page_url" target="_blank" class="text-truncate d-inline-block" style="max-width: 300px;">
                          {{ result.page_url }}
                          <i class="fas fa-external-link-alt ms-1 small"></i>
                        </a>
                      </td>
                      <td>{{ result.page_title || 'N/A' }}</td>
                      <td class="text-center">
                        <span class="badge bg-danger">{{ result.error_count }}</span>
                      </td>
                      <td class="text-center">
                        <span class="badge bg-warning">{{ result.warning_count }}</span>
                      </td>
                      <td class="text-center">
                        <span class="badge bg-info">{{ result.notice_count }}</span>
                      </td>
                      <td class="text-center text-nowrap">
                        <button
                          class="btn btn-sm btn-outline-primary me-1"
                          @click="viewDetails(result)"
                        >
                          <i class="fas fa-eye"></i>
                          Details
                        </button>
                        <button
                          :class="hasWorkOrder(result) ? 'btn btn-sm btn-success' : 'btn btn-sm btn-outline-secondary'"
                          @click="generatePageWorkOrder(result)"
                          :disabled="result._generatingWO"
                          :title="hasWorkOrder(result) ? 'View AI Work Order' : 'Generate AI Work Order'"
                        >
                          <i :class="result._generatingWO ? 'fas fa-spinner fa-spin' : 'fas fa-robot'"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Audits Yet -->
      <div class="row" v-if="!latestAudit && !loading">
        <div class="col-12">
          <div class="alert alert-info text-center">
            <i class="fas fa-info-circle fa-3x mb-3"></i>
            <h4>No audits have been run yet</h4>
            <p>Click "Run Audit Now" to scan this website for accessibility issues.</p>
          </div>
        </div>
      </div>

      <!-- Document Scanning (Media Library + Meetings) -->
      <div class="row mb-4">
        <div class="col-12">
          <ClientDocumentTabs :clientId="clientId" />
        </div>
      </div>

      <!-- Single PDF Upload Scanner -->
      <div class="row mb-4">
        <div class="col-12">
          <PdfScanner />
        </div>
      </div>
    </template>

    <!-- Schedule Modal -->
    <ScheduleModal
      v-if="showScheduleModal"
      :clientId="clientId"
      :currentSchedule="schedule"
      @close="showScheduleModal = false"
      @saved="onScheduleSaved"
    />

    <!-- Details Modal -->
    <DetailsModal
      v-if="showDetailsModal"
      :result="selectedResult"
      @close="showDetailsModal = false"
    />

    <!-- Work Order Modal -->
    <!-- Generating Progress Modal -->
    <div v-if="showGeneratingModal" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.6);">
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body text-center py-4">
            <i class="fas fa-robot fa-2x text-info mb-3"></i>
            <h6>Generating AI Work Order</h6>
            <div class="progress mb-3" style="height:8px;">
              <div class="progress-bar bg-info progress-bar-striped progress-bar-animated" :style="{ width: (generatingStep * 25) + '%' }"></div>
            </div>
            <p class="small text-muted mb-0">{{ generatingStepText }}</p>
            <div class="mt-2">
              <span v-for="s in 4" :key="s" class="badge me-1" :class="s <= generatingStep ? 'bg-info' : 'bg-light text-muted'">{{ s }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Work Order Modal -->
    <div v-if="showWorkOrder && workOrderData" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);" @click.self="showWorkOrder = false">
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <div>
              <h5 class="modal-title">
                <i class="fas fa-robot me-2"></i>
                AI Work Order
              </h5>
              <small class="text-muted">
                Generated by Claude
                <span v-if="workOrderData._pageTitle"> for {{ workOrderData._pageTitle }}</span>
                <span v-else-if="client"> for {{ client.name }}</span>
              </small>
              <br v-if="workOrderData._pageUrl">
              <small v-if="workOrderData._pageUrl" class="text-muted">
                <a :href="workOrderData._pageUrl" target="_blank">{{ workOrderData._pageUrl }} <i class="fas fa-external-link-alt"></i></a>
              </small>
            </div>
            <button type="button" class="btn-close" @click="showWorkOrder = false"></button>
          </div>
          <div class="modal-body">
            <!-- Annotated Screenshot -->
            <div v-if="workOrderData.screenshot_url" class="mb-4">
              <h6 class="text-muted mb-2"><i class="fas fa-camera me-1"></i> Annotated Screenshot</h6>
              <div style="max-height:400px; overflow-y:auto; border:1px solid #dee2e6; border-radius:8px;">
                <img :src="workOrderData.screenshot_url" alt="Annotated screenshot" style="width:100%;" />
              </div>
              <small class="text-muted">Numbered markers on the screenshot match the task numbers below.</small>
            </div>

            <div class="alert alert-info mb-4" v-if="workOrderData.summary">
              <i class="fas fa-info-circle me-2"></i>
              {{ workOrderData.summary }}
            </div>

            <div class="row">
              <div class="col-md-6">
                <h6 class="mb-3">
                  <i class="fas fa-palette me-1 text-warning"></i>
                  Content & Design Tasks
                  <span class="badge bg-warning text-dark ms-1">{{ (workOrderData.content_design_tasks || []).length }}</span>
                  <br><small class="text-muted fw-normal">For PMs and Designers</small>
                </h6>
                <div v-for="(task, i) in (workOrderData.content_design_tasks || [])" :key="'cd-' + i" class="card mb-2">
                  <div class="card-body p-3">
                    <div class="d-flex justify-content-between align-items-start mb-1">
                      <div class="d-flex align-items-center">
                        <span v-if="task.marker" class="badge bg-dark me-2" style="min-width:28px;">#{{ task.marker }}</span>
                        <strong>{{ task.title }}</strong>
                      </div>
                      <span class="badge" :class="priorityBadge(task.priority)">{{ task.priority }}</span>
                    </div>
                    <p class="mb-2 small">{{ task.description }}</p>
                    <div class="bg-light p-2 rounded mb-2" style="white-space:pre-wrap;">
                      <small><strong>Fix:</strong> {{ task.fix }}</small>
                    </div>
                    <small v-if="task.element_selector" class="text-muted d-block mb-1">
                      <i class="fas fa-crosshairs me-1"></i>
                      <code style="font-size:11px;">{{ task.element_selector }}</code>
                    </small>
                    <div class="d-flex justify-content-between">
                      <small class="text-muted" v-if="task.wcag">WCAG {{ task.wcag }}</small>
                      <small class="text-muted" v-if="task.instances">{{ task.instances }} instance{{ task.instances > 1 ? 's' : '' }}</small>
                    </div>
                  </div>
                </div>
                <p v-if="!(workOrderData.content_design_tasks || []).length" class="text-muted">No content/design tasks found.</p>
              </div>

              <div class="col-md-6">
                <h6 class="mb-3">
                  <i class="fas fa-code me-1 text-primary"></i>
                  Code & Structure Tasks
                  <span class="badge bg-primary ms-1">{{ (workOrderData.code_structure_tasks || []).length }}</span>
                  <br><small class="text-muted fw-normal">For Developers</small>
                </h6>
                <div v-for="(task, i) in (workOrderData.code_structure_tasks || [])" :key="'cs-' + i" class="card mb-2">
                  <div class="card-body p-3">
                    <div class="d-flex justify-content-between align-items-start mb-1">
                      <div class="d-flex align-items-center">
                        <span v-if="task.marker" class="badge bg-dark me-2" style="min-width:28px;">#{{ task.marker }}</span>
                        <strong>{{ task.title }}</strong>
                      </div>
                      <span class="badge" :class="priorityBadge(task.priority)">{{ task.priority }}</span>
                    </div>
                    <p class="mb-2 small">{{ task.description }}</p>
                    <div class="bg-light p-2 rounded mb-2" style="white-space:pre-wrap;">
                      <small><strong>Fix:</strong> {{ task.fix }}</small>
                    </div>
                    <small v-if="task.element_selector" class="text-muted d-block mb-1">
                      <i class="fas fa-crosshairs me-1"></i>
                      <code style="font-size:11px;">{{ task.element_selector }}</code>
                    </small>
                    <div class="d-flex justify-content-between">
                      <small class="text-muted" v-if="task.wcag">WCAG {{ task.wcag }}</small>
                      <small class="text-muted" v-if="task.instances">{{ task.instances }} instance{{ task.instances > 1 ? 's' : '' }}</small>
                    </div>
                  </div>
                </div>
                <p v-if="!(workOrderData.code_structure_tasks || []).length" class="text-muted">No code/structure tasks found.</p>
              </div>
            </div>

            <div v-if="workOrderData.conflicts_resolved && workOrderData.conflicts_resolved.length" class="mt-3">
              <h6 class="text-muted"><i class="fas fa-exchange-alt me-1"></i> IBM/WAVE Conflicts Resolved</h6>
              <ul class="small text-muted">
                <li v-for="(conflict, i) in workOrderData.conflicts_resolved" :key="'c-' + i">{{ conflict }}</li>
              </ul>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showWorkOrder = false">Close</button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Breadcrumb from '@/components/juniper/breadcrumb/Breadcrumb.vue'
import ScheduleModal from '@/components/juniper/ada-auditor/ScheduleModal.vue'
import DetailsModal from '@/components/juniper/ada-auditor/DetailsModal.vue'
import PdfScanner from '@/components/juniper/ada-auditor/PdfScanner.vue'
import ClientDocumentTabs from '@/components/juniper/ada-auditor/ClientDocumentTabs.vue'
import juniperUserMixin from '@/mixins/juniperUserMixin'
import api from '@/api'
import juniperApi from '@/api/juniperApi'
import Chart from 'chart.js/auto'

export default {
  name: 'ClientAdaAuditor',
  components: { Breadcrumb, ScheduleModal, DetailsModal, PdfScanner, ClientDocumentTabs },
  mixins: [juniperUserMixin],

  data() {
    return {
      clientId: this.$route.params.id,
      client: null,
      loading: false,
      isRunning: false,
      latestAudit: null,
      auditResults: [],
      trendData: [],
      schedule: null,
      filterType: 'all',
      showScheduleModal: false,
      showDetailsModal: false,
      selectedResult: null,
      chart: null,
      pollInterval: null,
      auditType: 'homepage',
      includeWave: false,
      waveCreditsRemaining: null,
      remediationProgress: null,
      generatingAnalysis: false,
      showGeneratingModal: false,
      generatingStep: 0,
      generatingStepText: '',
      showWorkOrder: false,
      workOrderData: null,
      selectedPages: [],
      clientPages: [],
      selectedAuditId: null,
      expandedAuditId: null,
      expandedAuditResults: [],
      loadingExpandedAudit: false,
    }
  },

  computed: {
    scoreClass() {
      const score = (this.latestAudit && this.latestAudit.compliance_score) || 0
      if (score >= 90) return 'text-success'
      if (score >= 70) return 'text-warning'
      return 'text-danger'
    },

    filteredResults() {
      if (this.filterType === 'all') return this.auditResults

      return this.auditResults.filter(result => {
        switch(this.filterType) {
          case 'errors': return result.error_count > 0
          case 'warnings': return result.warning_count > 0
          case 'notices': return result.notice_count > 0
          default: return true
        }
      })
    }
  },

  mounted() {
    this.loadData()
  },

  beforeDestroy() {
    if (this.pollInterval) clearInterval(this.pollInterval)
    if (this.chart) this.chart.destroy()
  },

  methods: {
    async loadData() {
      this.loading = true
      try {
        await Promise.all([
          this.loadClient(),
          this.loadClientPages(),
          this.loadLatestAudit(),
          this.loadAuditHistory(),
          this.loadSchedule(),
          this.loadWaveCredits(),
          this.loadRemediationProgress(),
        ])
      } catch (error) {
        console.error('Failed to load data:', error)
        if (this.$toast) this.$toast.error('Failed to load audit data')
      } finally {
        this.loading = false
      }
    },

    async loadClient() {
      try {
        const response = await juniperApi.get(`/clients/${this.clientId}`)
        if (response && response.data) {
          this.client = response.data
        }
      } catch (error) {
        console.error('Failed to load client:', error)
        if (this.$toast) this.$toast.error('Failed to load client data')
      }
    },

    async loadClientPages() {
      try {
        const response = await api.get(`/juniper/ada-auditor/clients/${this.clientId}/pages`)
        if (response && response.data && response.data.pages) {
          this.clientPages = response.data.pages
        }
      } catch (error) {
        console.error('Failed to load client pages:', error)
        // Don't show error toast as this is optional
      }
    },

    async loadLatestAudit() {
      try {
        const response = await api.get(`/juniper/ada-auditor/clients/${this.clientId}/audit/latest`)
        if (response && response.data) {
          this.latestAudit = response.data
          this.auditResults = response.data.results || []

          // Check if audit is running
          if (this.latestAudit && (this.latestAudit.status === 'running' || this.latestAudit.status === 'pending')) {
            this.isRunning = true
            this.startPolling()
          }
        }
      } catch (error) {
        // Only clear data if it's a 404 (no audits exist)
        if (error.response && error.response.status === 404) {
          this.latestAudit = null
          this.auditResults = []
        } else if (!this.latestAudit) {
          // If this is initial load (no existing data), set defaults
          console.error('Failed to load latest audit on initial load:', error)
          this.latestAudit = null
          this.auditResults = []
        } else {
          // During polling, if we already have data, keep it on error
          console.error('Failed to refresh audit (keeping existing data):', error)
          // Don't clear latestAudit and auditResults - keep showing what we have
        }
      }
    },

    async loadAuditHistory() {
      try {
        const response = await api.get(`/juniper/ada-auditor/clients/${this.clientId}/audit/history`)
        if (response && response.data) {
          const newData = response.data.audits || []

          // On initial load or when data changes, update and re-render
          const isInitialLoad = this.trendData.length === 0 && newData.length > 0
          const dataChanged = JSON.stringify(newData) !== JSON.stringify(this.trendData)

          if (isInitialLoad || dataChanged) {
            this.trendData = newData

            // Render chart only when data changes or on initial load
            if (newData.length > 0) {
              this.$nextTick(() => {
                this.renderChart()
              })
              // Also try again after a short delay in case the canvas isn't ready
              setTimeout(() => {
                if (this.$refs.trendChart && (!this.chart || this.chart.data.datasets[0].data.length === 0)) {
                  console.log('Re-rendering chart after delay')
                  this.renderChart()
                }
              }, 500)
            }
          }
        }
      } catch (error) {
        console.error('Failed to load audit history:', error)
        // Don't clear trendData on error - keep existing chart if we have it
        if (this.trendData.length === 0) {
          // Only on initial load failure, show empty state
          this.trendData = []
        }
      }
    },

    async loadSchedule() {
      try {
        const response = await api.get(`/juniper/ada-auditor/clients/${this.clientId}/schedule`)
        if (response && response.data) {
          this.schedule = response.data
        }
      } catch (error) {
        this.schedule = null
      }
    },

    async loadRemediationProgress() {
      if (!this.latestAudit) return
      try {
        const response = await api.get(`/juniper/ada-auditor/audits/${this.latestAudit.id}/remediation-log`)
        if (response && response.data && response.data.success) {
          this.remediationProgress = response.data.progress
        }
      } catch (error) {
        // Not available yet
      }
    },

    async loadWaveCredits() {
      try {
        const response = await api.get('/juniper/ada-auditor/wave-credits')
        if (response && response.data) {
          this.waveCreditsRemaining = response.data.credits_remaining
        }
      } catch (error) {
        // WAVE credits not available
      }
    },

    async generateWorkOrder() {
      if (!this.latestAudit) return
      if (this.latestAudit.analysis_result && !this.showWorkOrder) {
        this.workOrderData = this.latestAudit.analysis_result
        this.showWorkOrder = true
        return
      }
      this.generatingAnalysis = true
      try {
        const response = await api.post(`/juniper/ada-auditor/audits/${this.latestAudit.id}/generate-analysis`)
        if (response && response.data && response.data.success) {
          this.workOrderData = response.data.analysis
          this.latestAudit.analysis_result = response.data.analysis
          this.showWorkOrder = true
          if (this.$toast) this.$toast.success('Work order generated')
        } else {
          if (this.$toast) this.$toast.error(response.data?.message || 'Failed to generate work order')
        }
      } catch (error) {
        console.error('Failed to generate work order:', error)
        if (this.$toast) this.$toast.error('Failed to generate work order')
      } finally {
        this.generatingAnalysis = false
      }
    },

    async generatePageWorkOrder(result) {
      // If cached, show immediately
      if (this.hasWorkOrder(result)) {
        const cacheKey = 'page_' + result.id
        this.workOrderData = {
          ...this.latestAudit.analysis_result[cacheKey],
          _pageUrl: result.page_url,
          _pageTitle: result.page_title,
          _resultId: result.id,
          _cached: true,
        }
        this.showWorkOrder = true
        return
      }

      // First time - show progress modal
      this.$set(result, '_generatingWO', true)
      this.generatingAnalysis = true
      this.showGeneratingModal = true
      this.generatingStep = 1
      this.generatingStepText = 'Taking annotated screenshot of the page...'

      try {
        // Simulate progress steps (the backend does screenshot + Claude in one call)
        const progressTimer = setInterval(() => {
          if (this.generatingStep < 3) {
            this.generatingStep++
            if (this.generatingStep === 2) this.generatingStepText = 'Analyzing issues with Claude AI...'
            if (this.generatingStep === 3) this.generatingStepText = 'Generating work order tasks...'
          }
        }, 8000)

        const response = await api.post(`/juniper/ada-auditor/results/${result.id}/generate-page-analysis`)
        clearInterval(progressTimer)

        if (response && response.data && response.data.success) {
          this.generatingStep = 4
          this.generatingStepText = 'Done!'

          this.workOrderData = {
            ...response.data.analysis,
            _pageUrl: response.data.page_url,
            _pageTitle: response.data.page_title,
            _resultId: result.id,
            _cached: false,
          }

          // Update the latestAudit cache so the button turns green
          if (this.latestAudit) {
            if (!this.latestAudit.analysis_result) this.$set(this.latestAudit, 'analysis_result', {})
            this.$set(this.latestAudit.analysis_result, 'page_' + result.id, response.data.analysis)
          }

          setTimeout(() => {
            this.showGeneratingModal = false
            this.showWorkOrder = true
          }, 500)
        } else {
          this.showGeneratingModal = false
          if (this.$toast) this.$toast.error(response.data?.message || 'Failed to generate work order')
        }
      } catch (error) {
        this.showGeneratingModal = false
        console.error('Failed to generate page work order:', error)
        if (this.$toast) this.$toast.error('Failed to generate work order')
      } finally {
        this.$set(result, '_generatingWO', false)
        this.generatingAnalysis = false
      }
    },

    async startAudit() {
      if (this.isRunning) return

      const auditUrl = this.client && this.client.staging ? this.client.staging_url : (this.client ? this.client.url : null)
      if (!this.client || !auditUrl) {
        if (this.$toast) this.$toast.error('Client website URL is not configured')
        return
      }

      // Validate specific pages selection
      if (this.auditType === 'specific' && this.selectedPages.length === 0) {
        if (this.$toast) this.$toast.error('Please select at least one page to audit')
        return
      }

      // WAVE confirmation for multi-page audits
      if (this.includeWave && this.auditType !== 'homepage') {
        const pageCount = this.auditType === 'specific' ? this.selectedPages.length : 'up to 50'
        const result = await this.$swal.fire({
          title: 'Include WAVE Analysis?',
          html: `WAVE analysis costs <strong>3 credits per page</strong>.<br>This audit will scan <strong>${pageCount} pages</strong>.${this.waveCreditsRemaining !== null ? `<br><br>You have <strong>${this.waveCreditsRemaining}</strong> credits remaining.` : ''}`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, include WAVE',
          cancelButtonText: 'Cancel',
        })
        if (!result.isConfirmed) return
      }

      this.isRunning = true
      try {
        await api.post(`/juniper/ada-auditor/clients/${this.clientId}/audit/start`, {
          website_url: auditUrl,
          audit_type: this.auditType,
          include_wave: this.includeWave,
          specific_pages: this.auditType === 'specific' ? this.selectedPages : []
        })

        const typeLabel = {
          full: 'Full website',
          homepage: 'Homepage',
          specific: `${this.selectedPages.length} page(s)`
        }[this.auditType]

        if (this.$toast) this.$toast.success(`${typeLabel} audit started! This may take a few minutes.`)
        this.startPolling()
      } catch (error) {
        console.error('Failed to start audit:', error)
        const message = (error.response && error.response.data && error.response.data.message) || 'Failed to start audit'
        if (this.$toast) this.$toast.error(message)
        this.isRunning = false
      }
    },

    startPolling() {
      // Clear any existing polling interval to prevent duplicates
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
      }
      // Poll every 10 seconds to check audit status
      this.pollInterval = setInterval(async () => {
        await this.loadLatestAudit()

        if (this.latestAudit && (this.latestAudit.status === 'completed' || this.latestAudit.status === 'failed')) {
          this.isRunning = false
          clearInterval(this.pollInterval)
          this.pollInterval = null

          if (this.latestAudit.status === 'completed') {
            if (this.$toast) this.$toast.success('Audit completed successfully!')
            await this.loadAuditHistory() // Refresh trend data
          } else {
            if (this.$toast) this.$toast.error('Audit failed. Please try again.')
          }
        }
      }, 10000)
    },

    async exportReport(type = 'client') {
      if (!this.latestAudit) return

      try {
        const response = await api.get(
          `/juniper/ada-auditor/audits/${this.latestAudit.id}/report?type=${type}`,
          { responseType: 'blob' }
        )

        // Download PDF
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        const date = new Date().toISOString().split('T')[0]
        const reportLabel = type === 'client' ? 'client-report' : 'technical-report'
        link.setAttribute('download', `ada-audit-${reportLabel}-${this.clientId}-${date}.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()

        const reportType = type === 'client' ? 'Client' : 'Technical'
        if (this.$toast) this.$toast.success(`${reportType} report downloaded`)
      } catch (error) {
        console.error('Failed to export report:', error)
        if (this.$toast) this.$toast.error('Failed to export report')
      }
    },

    async exportHistoryReport(auditId) {
      try {
        const response = await api.get(
          `/juniper/ada-auditor/audits/${auditId}/report`,
          { responseType: 'blob' }
        )

        // Download PDF
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        const date = new Date().toISOString().split('T')[0]
        link.setAttribute('download', `ada-audit-${this.clientId}-${date}.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()

        if (this.$toast) this.$toast.success('Report downloaded')
      } catch (error) {
        console.error('Failed to export report:', error)
        if (this.$toast) this.$toast.error('Failed to export report')
      }
    },

    viewDetails(result) {
      this.selectedResult = result
      this.showDetailsModal = true
    },

    onScheduleSaved(newSchedule) {
      this.schedule = newSchedule
      this.showScheduleModal = false
      if (this.$toast) this.$toast.success('Schedule updated successfully')
    },

    renderChart() {
      if (!this.$refs.trendChart || this.trendData.length === 0) return

      if (this.chart) this.chart.destroy()

      const ctx = this.$refs.trendChart.getContext('2d')
      this.chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.trendData.map(d => this.formatDateShort(d.created_at)).reverse(),
          datasets: [
            {
              label: 'Overall Compliance Score',
              data: this.trendData.map(d => d.compliance_score).reverse(),
              borderColor: 'rgb(75, 192, 192)',
              backgroundColor: 'rgba(75, 192, 192, 0.2)',
              tension: 0.4,
              yAxisID: 'y',
              borderWidth: 3
            },
            {
              label: 'IBM Code Score',
              data: this.trendData.map(d => d.ibm_code_score || null).reverse(),
              borderColor: 'rgb(54, 162, 235)',
              backgroundColor: 'rgba(54, 162, 235, 0.2)',
              tension: 0.4,
              yAxisID: 'y',
              borderDash: [5, 5]
            },
            {
              label: 'WAVE Visual Score',
              data: this.trendData.map(d => d.wave_visual_score || null).reverse(),
              borderColor: 'rgb(153, 102, 255)',
              backgroundColor: 'rgba(153, 102, 255, 0.2)',
              tension: 0.4,
              yAxisID: 'y',
              borderDash: [2, 2]
            },
            {
              label: 'Total Errors',
              data: this.trendData.map(d => d.total_errors).reverse(),
              borderColor: 'rgb(255, 99, 132)',
              backgroundColor: 'rgba(255, 99, 132, 0.2)',
              tension: 0.4,
              yAxisID: 'y1',
            }
          ]
        },
        options: {
          responsive: true,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          scales: {
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              title: {
                display: true,
                text: 'Compliance Score'
              },
              min: 0,
              max: 100
            },
            y1: {
              type: 'linear',
              display: true,
              position: 'right',
              title: {
                display: true,
                text: 'Error Count'
              },
              grid: {
                drawOnChartArea: false,
              },
            },
          }
        }
      })
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    },

    formatDateShort(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric'
      })
    },

    auditTypeLabel(type) {
      const labels = {
        'full': 'Full Website',
        'homepage': 'Homepage',
        'specific': 'Specific Pages'
      }
      return labels[type] || type
    },

    auditTypeBadgeClass(type) {
      const classes = {
        'full': 'bg-primary',
        'homepage': 'bg-success',
        'specific': 'bg-info'
      }
      return classes[type] || 'bg-secondary'
    },

    getScoreClass(score) {
      if (score >= 90) return 'text-success'
      if (score >= 70) return 'text-warning'
      return 'text-danger'
    },

    hasWorkOrder(result) {
      if (!this.latestAudit || !this.latestAudit.analysis_result) return false
      const cacheKey = 'page_' + result.id
      return !!this.latestAudit.analysis_result[cacheKey]
    },

    priorityBadge(priority) {
      const map = { critical: 'bg-danger', high: 'bg-warning text-dark', medium: 'bg-info', low: 'bg-secondary' }
      return map[priority] || 'bg-secondary'
    },

    async toggleAuditExpand(auditId) {
      // If clicking the same audit, collapse it
      if (this.expandedAuditId === auditId) {
        this.expandedAuditId = null
        this.expandedAuditResults = []
        return
      }

      // Expand the new audit
      this.expandedAuditId = auditId
      this.loadingExpandedAudit = true
      this.expandedAuditResults = []

      try {
        // Fetch the specific audit with its results
        const response = await api.get(`/juniper/ada-auditor/audits/${auditId}`)

        if (response && response.data) {
          this.expandedAuditResults = response.data.results || []
        }
      } catch (error) {
        console.error('Failed to load audit details:', error)
        if (this.$toast) this.$toast.error('Failed to load audit results')
        this.expandedAuditId = null
      } finally {
        this.loadingExpandedAudit = false
      }
    },

    truncateUrl(url) {
      if (!url) return ''
      if (url.length <= 60) return url
      return url.substring(0, 57) + '...'
    }
  }
}
</script>

<style scoped>
.text-truncate {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Expandable row styles - Clean and simple */
tr.expanded-row td {
  background-color: #fafafa !important;
  padding: 0 !important;
  border-top: 2px solid #e0e0e0;
  border-bottom: 2px solid #e0e0e0;
}

tr[style*="cursor: pointer"]:hover {
  background-color: #f5f5f5;
}

tr.table-active {
  background-color: #eeeeee !important;
  font-weight: 500;
}

/* Expanded content container with proper indentation */
.expanded-content {
  background-color: #fafafa !important;
  padding: 20px 30px 20px 60px !important; /* Large left indent */
  margin: 10px 20px;
  border-left: 4px solid #999;
  border-radius: 4px;
}

.expanded-row h6 {
  color: #555;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 15px;
  margin-left: -20px; /* Pull header slightly left for visual hierarchy */
}

/* Inner table - completely independent layout */
.expanded-row .table-responsive {
  margin-top: 10px;
}

.expanded-row .table {
  margin-bottom: 0;
  background-color: white;
  width: 100%;
  table-layout: auto; /* Let table determine its own column widths */
}

.expanded-row .table thead {
  background-color: #f5f5f5;
}

.expanded-row .table thead th {
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #ddd;
  white-space: nowrap;
}

.expanded-row .table tbody tr:hover {
  background-color: #f9f9f9;
}

.expanded-row .table tbody td {
  vertical-align: middle;
}

/* Chevron */
.fa-chevron-right,
.fa-chevron-down {
  transition: transform 0.15s ease;
  color: #666;
}
</style>
